import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, MapPin, LocateFixed, Loader2, ChevronRight, Route as RouteIcon, Camera } from "lucide-react";
import { useField } from "../FieldContext";
import { Mono, assetReadiness } from "../ui";
import { getPosition } from "../lib/geo";

export default function EventDetail() {
  const { claimId } = useParams();
  const { store, version, bump } = useField();
  const [claim, setClaim] = useState(null);
  const [assets, setAssets] = useState([]);
  const [runs, setRuns] = useState([]);
  const [locating, setLocating] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      setClaim(await store.getRec("claims", claimId));
      const [as, rn, allPhotos] = await Promise.all([store.getAssets(claimId), store.getRuns(claimId), store.all("photos")]);
      setAssets(as.map((a) => ({ ...a, _photos: allPhotos.filter((p) => p.asset_id === a.id) })));
      setRuns(rn);
    })();
  }, [store, claimId, version]);

  const setLocation = async () => {
    setLocating(true);
    try {
      const pos = await getPosition();
      await store.locateClaim(claimId, pos.latitude, pos.longitude);
      bump();
    } catch (e) {
      alert("Could not get your location. Enable GPS and try again.");
    } finally {
      setLocating(false);
    }
  };

  if (!claim) return <div className="text-white/50 text-sm" data-testid="field-event-loading">Loading event…</div>;

  return (
    <div className="space-y-5" data-testid="field-event-detail">
      <div>
        <Mono className="text-gold-400 text-[0.6rem]">{claim.assessing_body || claim.region} · {claim.agrn || "event"}</Mono>
        <h1 className="font-heading text-2xl mt-1 leading-snug">{claim.name}</h1>
        {claim.council && <p className="text-white/50 text-sm mt-0.5">{claim.council}</p>}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={setLocation} disabled={locating} data-testid="field-set-location" className="border border-gold-500/25 bg-navy-900/50 rounded-sm px-3 py-2.5 text-xs text-gold-300 flex items-center justify-center gap-1.5">
          {locating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LocateFixed className="h-3.5 w-3.5" />} Set event location
        </button>
        <button onClick={() => nav("/field/map")} data-testid="field-open-map" className="border border-gold-500/25 bg-navy-900/50 rounded-sm px-3 py-2.5 text-xs text-white/70 flex items-center justify-center gap-1.5">
          <MapPin className="h-3.5 w-3.5 text-emerald-400" /> {claim.latitude != null ? "View on map" : "No point set"}
        </button>
      </div>

      {runs.length > 0 && (
        <div className="space-y-2">
          <Mono className="text-white/45 text-[0.55rem]">Inspection runs</Mono>
          {runs.map((r) => (
            <button key={r.id} onClick={() => nav(`/field/run/${r.id}`)} data-testid={`field-run-${r.id}`} className="w-full flex items-center justify-between border border-gold-500/20 bg-navy-900/40 rounded-sm px-3 py-2.5 text-sm">
              <span className="flex items-center gap-2"><RouteIcon className="h-4 w-4 text-gold-400" /> {r.name}</span>
              <span className="flex items-center gap-2 text-white/50 text-xs">{(r.stops || []).filter((s) => s.visited).length}/{(r.stops || []).length} · {r.status}<ChevronRight className="h-4 w-4" /></span>
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <Mono className="text-white/45 text-[0.55rem]">Assets · {assets.length}</Mono>
        <button onClick={() => nav(`/field/event/${claimId}/asset/new`)} data-testid="field-add-asset" className="inline-flex items-center gap-1.5 bg-gold-500 text-navy-950 font-semibold text-xs px-3 py-2 rounded-sm">
          <Plus className="h-3.5 w-3.5" /> Add asset
        </button>
      </div>

      {assets.length === 0 ? (
        <div className="border border-dashed border-gold-500/25 rounded-sm p-8 text-center text-white/45 text-sm" data-testid="field-assets-empty">
          No assets yet. Add the damaged asset, then capture 3-phase photo evidence against it.
        </div>
      ) : (
        <div className="space-y-3">
          {assets.map((a) => {
            const r = assetReadiness(a, a._photos);
            return (
              <button key={a.id} onClick={() => nav(`/field/asset/${a.id}`)} data-testid={`field-asset-${a.id}`} className="w-full text-left border border-gold-500/25 bg-navy-900/50 rounded-sm p-4 active:bg-navy-800">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-base">{a.name}</h3>
                  {a.synced === false && <span className="text-[0.55rem] text-amber-300 border border-amber-500/40 px-1.5 py-0.5 rounded-sm">unsynced</span>}
                </div>
                <p className="text-white/45 text-xs mt-0.5">{a.asset_type} · {a.funding_measure}</p>
                <div className="flex items-center justify-between mt-3">
                  <span className="flex items-center gap-1.5 text-xs text-white/60"><Camera className="h-3.5 w-3.5 text-gold-500/60" /> {r.total} photos</span>
                  <span className="flex items-center gap-2 text-xs">
                    <span className={`h-2 w-2 rounded-full ${r.pct === 100 ? "bg-emerald-400" : r.pct >= 50 ? "bg-gold-400" : "bg-red-400"}`} />
                    <span className="text-white/60">{r.pct}% ready</span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
